import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Button, Dimmer, Form, Icon, Loader, Message, Pagination } from 'semantic-ui-react';
import store from '../../../store';
import { toS3Url } from '../../../utils/shared.utils';
import './return.scss';

const PAGE_SIZE = 24;

interface AdminProductRow {
    id: number;
    name: string;
    slug?: string;
    providerName?: string;
    warehouseId?: number;
    count?: number;
    imageXsUrl?: string;
    imageSmUrl?: string;
    active?: number;
}

interface ReturnSearchPageProps {
    returnBasketIds: number[];
}

function ReturnSearchPage(props: ReturnSearchPageProps) {
    const history = useHistory();
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [products, setProducts] = useState<AdminProductRow[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token || token === 'undefined') {
            store.dispatch({ type: 'redirectUrl', data: window.location.pathname });
            history.push('/');
            return;
        }
        fetchProducts(token, page, appliedSearch);
    }, [page, appliedSearch]);

    const fetchProducts = (token: string, requestedPage: number, name: string) => {
        setLoading(true);
        setErrorMsg('');
        fetch(process.env.REACT_APP_API_URL + 'mongol/products', {
            method: 'POST',
            headers: { Accept: 'application/json', Authorization: token, 'Content-Type': 'application/json' },
            body: JSON.stringify({
                name: name || undefined,
                page: { page: requestedPage, size: PAGE_SIZE },
            }),
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(r)))
            .then((data) => {
                setProducts(Array.isArray(data?.data) ? data.data : []);
                if (data?.page) {
                    setTotalPages(Math.max(1, data.page.totalPages || 1));
                    setTotalRows(data.page.totalRows || 0);
                }
            })
            .catch(() => setErrorMsg('Жагсаалт ачаалахад алдаа гарлаа.'))
            .finally(() => setLoading(false));
    };

    const onSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        setAppliedSearch(search.trim());
    };

    const inBasket = useMemo(() => new Set(props.returnBasketIds), [props.returnBasketIds]);

    const addToReturn = (productId: number) => {
        store.dispatch({ type: 'RETURN_BASKET_ADD', productId });
    };

    const removeFromReturn = (productId: number) => {
        store.dispatch({ type: 'RETURN_BASKET_REMOVE', productId });
    };

    const clearBasket = () => {
        if (window.confirm('Сонгосон бараануудыг бүгдийг хасах уу?')) {
            store.dispatch({ type: 'RETURN_BASKET_CLEAR' });
        }
    };

    const basketCount = props.returnBasketIds.length;

    return (
        <div className="page-admin admin-return-search">
            <Dimmer active={loading} inverted>
                <Loader size="medium" />
            </Dimmer>
            <section className="admin-return-search-section">
                <div className="form-group">
                    <Link to="/mongol">
                        <Icon name="arrow left" />
                        <span className="underline">Буцах</span>
                    </Link>
                </div>
                <h1>Бараа буцаах</h1>
                <p className="muted">Буцаах бүтээгдэхүүнээ хайж олоод сагсанд нэмнэ үү.</p>

                <Form onSubmit={onSearchSubmit} className="return-search-form" data-testid="return-search-form">
                    <div data-testid="return-search-input">
                        <Form.Input
                            icon="search"
                            placeholder="Бүтээгдэхүүний нэрээр хайх"
                            value={search}
                            onChange={(_, d) => setSearch(String(d.value))}
                            action={{ icon: 'search', type: 'submit' }}
                        />
                    </div>
                </Form>

                {errorMsg && (
                    <Message negative size="small">
                        <Icon name="warning circle" /> {errorMsg}
                    </Message>
                )}
                <p className="muted">Нийт {totalRows} бүтээгдэхүүн</p>

                <div className="return-product-grid">
                    {products.map((p) => {
                        const selected = inBasket.has(p.id);
                        const canSelect = !!p.warehouseId;
                        return (
                            <div
                                key={p.id}
                                className={'return-product-card' + (selected ? ' selected' : '')}
                                data-testid="return-product-card"
                                data-product-id={p.id}
                            >
                                <div className="return-product-image">
                                    {p.imageSmUrl || p.imageXsUrl ? (
                                        <img alt={p.name} src={toS3Url(p.imageSmUrl || p.imageXsUrl)} />
                                    ) : (
                                        <div className="no-image"><Icon name="image" /></div>
                                    )}
                                </div>
                                <div className="return-product-body">
                                    <div className="return-product-name" title={p.name}>{p.name}</div>
                                    <div className="return-product-provider">{p.providerName || '–'}</div>
                                    <div className="return-product-count">Үлдэгдэл: <strong>{p.count != null ? p.count : '–'}</strong></div>
                                </div>
                                <div className="return-product-actions">
                                    {!canSelect ? (
                                        <Button size="small" disabled data-testid="return-product-disabled">Агуулахгүй</Button>
                                    ) : selected ? (
                                        <Button size="small" color="grey" onClick={() => removeFromReturn(p.id)} data-testid="return-product-remove">
                                            <Icon name="check" /> Сонгосон
                                        </Button>
                                    ) : (
                                        <Button size="small" color="red" onClick={() => addToReturn(p.id)} data-testid="return-product-add">
                                            <Icon name="plus" /> Буцаахад нэмэх
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {products.length === 0 && !loading && (
                    <p className="muted text-center">Бүтээгдэхүүн олдсонгүй.</p>
                )}

                {totalPages > 1 && (
                    <div className="flex-div margin-top-20 justify-content-center">
                        <Pagination
                            activePage={page}
                            totalPages={totalPages}
                            onPageChange={(_, d) => setPage(Number(d.activePage) || 1)}
                            firstItem={null}
                            lastItem={null}
                        />
                    </div>
                )}
            </section>

            {basketCount > 0 && (
                <div className="return-basket-bar" data-testid="return-basket-bar">
                    <div className="return-basket-bar-info" data-testid="return-basket-count">
                        <strong>{basketCount}</strong> бараа сонгосон
                    </div>
                    <div className="return-basket-bar-actions">
                        <Button basic color="grey" onClick={clearBasket} type="button" data-testid="return-basket-clear">
                            Цэвэрлэх
                        </Button>
                        <Button color="red" onClick={() => history.push('/mongol/return/confirm')} type="button" data-testid="return-basket-continue">
                            Буцаалт үргэлжлүүлэх <Icon name="arrow right" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

const mapStateToProps = (state: any) => ({
    returnBasketIds: state.returnBasket?.productIds || [],
});

export default connect(mapStateToProps)(ReturnSearchPage);
